import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { HTTPError, post } from "common/helpers/HTTP";
import { typedObjectEntries } from "common/helpers/utils";

import { useCampaignContext } from "../context/CampaignContext";
import useUpdateCampaign from "./useUpdateCampaign";

import type {
  AudienceFacets,
  AudienceFilters,
  AudienceProfile,
  CancelledTarget,
} from "../types";

async function addTargetToCampaignRequest(
  workspaceId: string,
  campaignId: string,
  profileUrl: string,
): Promise<AudienceProfile> {
  return (
    await post(`workspaces/${workspaceId}/campaigns/${campaignId}/audience`, {
      profile_url: profileUrl,
    })
  ).profile;
}

async function updateAudienceSizeRequest(
  workspaceId: string,
  campaignId: string,
  size: number,
): Promise<string[]> {
  return (
    await post(
      `workspaces/${workspaceId}/campaigns/${campaignId}/audience-size`,
      {
        size,
      },
    )
  ).removed;
}

async function removeProfileFromAudienceRequest(
  workspaceId: string,
  campaignId: string,
  options: { targetUrns: string[] } | { filters: AudienceFilters },
): Promise<{
  removed: string[];
  cancelled: string[];
}> {
  let payload = {};

  if ("targetUrns" in options) {
    payload = { target_urns: options.targetUrns };
  } else {
    const nonEmptyFilters = typedObjectEntries(options.filters)
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.trim() !== "";
        return value;
      })
      // biome-ignore lint/performance/noAccumulatingSpread: Small filter object, O(n²) is acceptable
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    payload = Object.keys(nonEmptyFilters).length > 0 ? nonEmptyFilters : {};
  }

  const { removed, cancelled } = await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/audience-delete`,
    payload,
  );

  return { removed, cancelled };
}

async function exportAudienceRequest(
  workspaceId: string,
  campaignId: string,
  filters: AudienceFilters,
): Promise<string> {
  const response = await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/audience-export`,
    filters,
  );

  return response.url;
}

export default function useAudienceMutations(filters?: AudienceFilters) {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);
  const { updateCachedCampaign, updateCachedCampaignInList } =
    useUpdateCampaign();
  const { id: campaignId, state: campaignState } = useCampaignContext();

  const { mutateAsync: updateAudienceSize, isPending: isUpdatingAudienceSize } =
    useMutation({
      mutationFn: ({ size }: { size: number }) =>
        updateAudienceSizeRequest(workspaceId, campaignId, size),
      onSuccess: (removedTargets, { size }) => {
        updateCachedCampaign(campaignId, (draftCampaign) => {
          draftCampaign.audience_size = size;
        });

        if (
          queryClient.getQueryData(
            campaignsKeys.audienceSearch(campaignId, filters),
          ) &&
          removedTargets.length
        ) {
          queryClient.setQueryData(
            campaignsKeys.audienceSearch(campaignId, filters),
            (prevData: {
              pages: {
                data: AudienceProfile[];
                facets: AudienceFacets;
              }[];
            }) => {
              const newPages = prevData.pages.map((page) => ({
                ...page,
                data: page.data.filter(
                  ({ urn }) => !removedTargets.includes(urn),
                ),
              }));

              updateCachedCampaign(campaignId, (draftCampaign) => {
                draftCampaign.target_count -= removedTargets.length;
              });

              return {
                ...prevData,
                pages: newPages,
              };
            },
          );
        }
      },
    });

  const { mutateAsync: removeProfiles, isPending: isRemovingProfiles } =
    useMutation({
      mutationFn: (
        options: { targetUrns: string[] } | { filters: AudienceFilters },
      ) => removeProfileFromAudienceRequest(workspaceId, campaignId, options),
      onSuccess: ({ removed, cancelled }) => {
        const profilesCount = removed.length + cancelled.length;

        // Update audience data (Campaign Details)
        if (queryClient.getQueryData(campaignsKeys.audience(campaignId))) {
          queryClient.setQueryData(
            campaignsKeys.audience(campaignId),
            (prevAudience: AudienceProfile[]) => {
              const filteredProfiles = prevAudience.filter(
                ({ urn }) => !removed.includes(urn),
              );
              if (cancelled.length) {
                return produce(filteredProfiles, (draftProfiles) => {
                  draftProfiles.forEach((draftProfile) => {
                    if (cancelled.includes(draftProfile.urn)) {
                      draftProfile.state = "cancelled";
                    }
                  });
                });
              }
            },
          );
        }

        if (
          cancelled.length &&
          queryClient.getQueryData(campaignsKeys.cancelledAudience(campaignId))
        ) {
          queryClient.setQueryData<CancelledTarget[]>(
            campaignsKeys.cancelledAudience(campaignId),
            (prevCancelledTargets) => [
              ...prevCancelledTargets,
              ...cancelled.map((urn) => ({
                target_urn: urn,
                transition: "manual_cancel" as const,
              })),
            ],
          );
        }

        // Update audienceSearch data (Create Campaign)
        if (
          queryClient.getQueryData(
            campaignsKeys.audienceSearch(campaignId, filters),
          )
        ) {
          queryClient.setQueryData<
            InfiniteData<{
              data: AudienceProfile[];
              facets: AudienceFacets;
            }>
          >(campaignsKeys.audienceSearch(campaignId, filters), (prevData) => {
            const newPages = prevData.pages.map((page) => ({
              ...page,
              data: page.data.filter(({ urn }) => !removed.includes(urn)),
            }));

            // Update target count by subtracting the number of removed profiles
            updateCachedCampaign(campaignId, (draftCampaign) => {
              draftCampaign.target_count -= removed.length;
            });

            return {
              ...prevData,
              pages: newPages,
            };
          });
        }

        toast.success(
          `${profilesCount} target${profilesCount > 1 ? "s" : ""} removed`,
        );
      },
    });

  const {
    mutateAsync: addTargetToCampaign,
    isPending: isAddingTargetToCampaign,
  } = useMutation({
    mutationFn: ({ profileUrl }: { profileUrl: string }) =>
      addTargetToCampaignRequest(workspaceId, campaignId, profileUrl),
    onSuccess: (profile) => {
      if (queryClient.getQueryData(campaignsKeys.audience(campaignId))) {
        queryClient.setQueryData(
          campaignsKeys.audience(campaignId),
          (prevAudience: AudienceProfile[]) => [...prevAudience, profile],
        );
      }
      updateCachedCampaignInList(
        campaignId,
        campaignState === "DONE" ? { state: "ACTIVE" } : { state: "DONE" },
      );
      updateCachedCampaign(campaignId, (draftCampaign) => {
        if (draftCampaign.state === "DONE") {
          draftCampaign.state = "ACTIVE";
        }
      });

      toast.success("Target added to the campaign");
    },
    onError: (err) => {
      toast.error(
        err instanceof HTTPError && err.serverMessage
          ? err.serverMessage
          : "Failed to add target to the campaign",
      );
    },
  });

  const { mutateAsync: exportAudience, isPending: isExporting } = useMutation({
    mutationFn: (audienceFilters: AudienceFilters) =>
      exportAudienceRequest(workspaceId, campaignId, audienceFilters),
    onError: () => {
      // Show an error toast to the user
      toast.error(
        "Failed to export audience, please try again later or contact support.",
      );
    },
  });

  return {
    updateAudienceSize,
    isUpdatingAudienceSize,
    removeProfiles,
    isRemovingProfiles,
    addTargetToCampaign,
    isAddingTargetToCampaign,
    exportAudience,
    isExporting,
  };
}
